import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Camera } from 'lucide-react';
import api from '../api';


const PublicEquipment = () => {
    const { uuid } = useParams();
    const [tool, setTool] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reporting, setReporting] = useState(false);
    
    // Form State
    const [report, setReport] = useState({ name: '', description: '', photo: null });
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        api.get(`public-equipment/lookup/${uuid}/`)
           .then(res => setTool(res.data))
           .catch(() => setTool(null))
           .finally(() => setLoading(false));
    }, [uuid]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', report.name);
        formData.append('description', report.description);
        if (report.photo) formData.append('photo', report.photo);

        try {
            await api.post(`public-equipment/report-fault/${uuid}/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSubmitted(true);
        } catch(err) { 
            alert("Failed to submit report. Please try again."); 
        }
    };

    if (loading) return <div style={{padding:'20px', color:'white', background:'#0f172a', minHeight:'100vh'}}>Loading...</div>;
    if (!tool) return <div style={{padding:'20px', color:'white', background:'#0f172a', minHeight:'100vh'}}>Invalid QR Code</div>;

    if (submitted) return (
        <div style={{minHeight:'100vh', background:'#0f172a', padding:'20px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'white', textAlign:'center'}}>
            <CheckCircle2 size={60} color="#10b981" style={{marginBottom:'20px'}}/>
            <h2>Report Submitted</h2>
            <p style={{color:'#94a3b8'}}>The site manager has been notified.</p>
            <p style={{color:'#94a3b8'}}>Status updated to <strong>MAINTENANCE</strong>.</p>
        </div>
    );

    return (
        <div style={{minHeight:'100vh', background:'#0f172a', padding:'20px', color:'white', fontFamily:'sans-serif'}}>
            <div style={{maxWidth:'500px', margin:'0 auto'}}>
                
                {/* TOOL CARD */}
                <div style={{background:'#1e293b', borderRadius:'12px', overflow:'hidden', border:'1px solid #334155', marginBottom:'20px'}}>
                    
                    {/* PHOTO AREA */}
                    <div style={{height:'200px', background:'#334155', display:'flex', alignItems:'center', justifyContent:'center'}}>
                         {tool.photo ? <img src={tool.photo} style={{width:'100%', height:'100%', objectFit:'cover'}} alt="Tool" /> : <Camera size={40} color="#94a3b8" />}
                    </div>
                    
                    {/* DETAILS */}
                    <div style={{padding:'20px'}}>
                        <h2 style={{margin:'0 0 5px 0', color:'white'}}>{tool.name}</h2>
                        <p style={{color:'#94a3b8', margin:0}}>{tool.company}</p>
                        
                        <div style={{marginTop:'20px', display:'flex', gap:'10px'}}>
                            
                            {/* STATUS BOX */}
                            <div style={{
                                flex:1, 
                                padding:'10px', 
                                background: tool.status_code === 'OPERATIONAL' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(250, 204, 21, 0.1)', 
                                borderRadius:'8px', 
                                textAlign:'center', 
                                border:`1px solid ${tool.status_code === 'OPERATIONAL' ? '#10b981' : '#facc15'}`
                            }}>
                                <span style={{display:'block', fontSize:'0.7rem', color:'#94a3b8', marginBottom:'4px'}}>STATUS</span>
                                <strong style={{
                                    color: tool.status_code === 'OPERATIONAL' ? '#10b981' : '#facc15',
                                    fontSize: '0.9rem',
                                    textTransform: 'uppercase'
                                }}>
                                    {/* Displays readable text like "Service/Maintenance" */}
                                    {tool.status || tool.status_code || "UNKNOWN"}
                                </strong>
                            </div>

                            {/* INSPECTION BOX */}
                            <div style={{flex:1, background:'#0f172a', padding:'10px', borderRadius:'8px', textAlign:'center', border:'1px solid #334155'}}>
                                <span style={{display:'block', fontSize:'0.7rem', color:'#94a3b8', marginBottom:'4px'}}>NEXT INSP.</span>
                                <strong style={{color:'white'}}>{tool.next_inspection || '-'}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* REPORT BUTTON OR FORM */}
                {!reporting ? (
                    <button onClick={() => setReporting(true)} style={{width:'100%', padding:'15px', background:'#ef4444', color:'white', border:'none', borderRadius:'12px', fontWeight:'bold', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', cursor:'pointer'}}>
                        <AlertTriangle /> Report a Fault
                    </button>
                ) : (
                    <form onSubmit={handleSubmit} style={{background:'#1e293b', padding:'20px', borderRadius:'12px', border:'1px solid #334155', animation:'fadeIn 0.3s'}}>
                        <h3 style={{marginTop:0, color:'white'}}>Report Fault</h3>
                        
                        <label style={{display:'block', fontSize:'0.85rem', color:'#94a3b8', marginBottom:'5px'}}>Your Name (Optional)</label>
                        <input value={report.name} onChange={e => setReport({...report, name: e.target.value})} style={{width:'100%', padding:'10px', background:'#0f172a', border:'1px solid #334155', borderRadius:'6px', color:'white', marginBottom:'15px', boxSizing:'border-box'}} />
                        
                        <label style={{display:'block', fontSize:'0.85rem', color:'#94a3b8', marginBottom:'5px'}}>Description of Issue</label>
                        <textarea required value={report.description} onChange={e => setReport({...report, description: e.target.value})} style={{width:'100%', padding:'10px', background:'#0f172a', border:'1px solid #334155', borderRadius:'6px', color:'white', marginBottom:'15px', minHeight:'80px', boxSizing:'border-box'}} />
                        
                        <label style={{display:'block', fontSize:'0.85rem', color:'#94a3b8', marginBottom:'5px'}}>Upload Photo</label>
                        <input type="file" accept="image/*" onChange={e => setReport({...report, photo: e.target.files[0]})} style={{width:'100%', padding:'10px', background:'#0f172a', border:'1px solid #334155', borderRadius:'6px', color:'white', marginBottom:'20px', boxSizing:'border-box'}} />
                        
                        <div style={{display:'flex', gap:'10px'}}>
                            <button type="button" onClick={() => setReporting(false)} style={{flex:1, padding:'12px', background:'transparent', border:'1px solid #334155', color:'#94a3b8', borderRadius:'6px', cursor:'pointer'}}>Cancel</button>
                            <button type="submit" style={{flex:1, padding:'12px', background:'#ef4444', border:'none', color:'white', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Submit Report</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
export default PublicEquipment;